import { skipToken, useQuery } from "@apollo/client/react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/system";
import { Cross } from "@pmag/icons";
import { IconButton } from "@pmag/ui-components";
import {
    type GQLComponentGroupParentGenericFragment,
    type GQLComponentSubGroupGenericFragment,
    type GQLHotspotFragment,
    type GQLSubGroupDetailSparePartFragment,
    type GQLSubGroupDetailSparePartsQuery,
    type GQLSubGroupDetailSparePartsQueryVariables,
} from "@src/graphql.generated";
import { useAttributeConfig } from "@src/hooks/useAttributeConfig";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { sortAndFilterSpareParts } from "@src/pages/subGroupDetail/SubgroupDetailPage.utils";
import { ControlPanel } from "@src/pages/subGroupDetail/subgroupDetailsListSection/controlPanel/ControlPanel";
import { ExplosionGraphic } from "@src/pages/subGroupDetail/subgroupDetailsListSection/explosionGraphic/ExplosionGraphic";
import { ExplosionGraphicModal } from "@src/pages/subGroupDetail/subgroupDetailsListSection/explosionGraphicModal/ExplosionGraphicModal";
import { ExplosionGraphicSmall } from "@src/pages/subGroupDetail/subgroupDetailsListSection/explosionGraphicSmall/ExplosionGraphicSmall";
import { SmallListItem } from "@src/pages/subGroupDetail/subgroupDetailsListSection/smallListItem/SmallListItem";
import { SmallListItemSkeleton } from "@src/pages/subGroupDetail/subgroupDetailsListSection/smallListItem/SmallListItemSkeleton";
import { SparePartsList } from "@src/pages/subGroupDetail/subgroupDetailsListSection/sparePartsList/SparePartsList";
import { SplitView } from "@src/pages/subGroupDetail/subgroupDetailsListSection/splitView/SplitView";
import { sparePartsQuery } from "@src/pages/subGroupDetail/subgroupDetailsListSection/SubgroupDetailsListSection.gql";
import { type ArticleNumberWithBrand, dispatchRequestArticleInfosForArticlesEvent, Events, type SPFArticleWithPriceInfo } from "@src/utils/events";
import { isSPFArticleInfo, isSPFArticleInfosEvent } from "@src/utils/typeGuard.utils";
import { uniqBy } from "lodash";
import type React from "react";
// eslint-disable-next-line no-duplicate-imports
import { type FunctionComponent } from "react";
// eslint-disable-next-line no-duplicate-imports
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { ContainerBox, ExplosionGraphicBox, ListContainer, SearchBoxPanel, StyledSearchBox } from "./SubgroupDetailsListSection.styles";

export const dataTestId = {
    container: "sparePartsFinder.subgroupPage.detailsList.container",
    outerControlPanel: "sparePartsFinder.subgroupPage.detailsList.outerControlPanel",
    searchBoxToggle: "sparePartsFinder.subgroupPage.detailsList.btn.searchToggle",
    searchBoxContainer: "sparePartsFinder.subgroupPage.detailsList.searchFieldContainer",
    searchBox: "sparePartsFinder.subgroupPage.detailsList.searchBox",
    btnClearSearch: "sparePartsFinder.subgroupPage.detailsList.btnClearSearch",
};

export interface SubgroupDetailsListSectionProps {
    brand: string;
    clientWishlist: string[];
    engineComponents: GQLComponentGroupParentGenericFragment[];
    frameComponents: GQLComponentGroupParentGenericFragment[];
    modelArticleNumber: string;
    modelId: string;
    selectedComponentGroup?: GQLComponentSubGroupGenericFragment;
}

export const SubgroupDetailsListSection: FunctionComponent<SubgroupDetailsListSectionProps> = ({
    brand,
    modelId,
    modelArticleNumber,
    clientWishlist,
    selectedComponentGroup,
    engineComponents,
    frameComponents,
}) => {
    const intl = useIntl();
    const theme = useTheme();
    const attributeConfig = useAttributeConfig();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const { componentGroupId } = useGlobalState();

    const [sparePartsWithPriceInfo, setSparePartsWithPriceInfo] = useState<SPFArticleWithPriceInfo[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [openExplosionGraphic, setOpenExplosionGraphic] = useState(false);
    const [hotspotHighlight, setHotspotHighlight] = useState<GQLHotspotFragment | null>(null);
    const [hotspotsHighlight, setHotspotsHighlight] = useState<GQLHotspotFragment[]>([]);

    const { data, loading, error } = useQuery<GQLSubGroupDetailSparePartsQuery, GQLSubGroupDetailSparePartsQueryVariables>(
        sparePartsQuery,
        selectedComponentGroup
            ? {
                  variables: {
                      componentGroupId: selectedComponentGroup.componentGroup.id,
                      modelId: modelId,
                      languageCode: intl.locale === "de" ? "de" : "en",
                  },
              }
            : skipToken,
    );

    useEffect(() => {
        setSparePartsWithPriceInfo([]);
    }, [componentGroupId]);

    useEffect(() => {
        if (attributeConfig.hasArticleInfos && data) {
            const articleNumbersWithBrand: ArticleNumberWithBrand[] = data.spareParts.nodes.flatMap((sparePart) =>
                sparePart.article?.articleNumber ? { articleNumber: sparePart.article.articleNumber, brand: brand } : [],
            );
            if (articleNumbersWithBrand.length > 0) dispatchRequestArticleInfosForArticlesEvent(articleNumbersWithBrand);
        }
    }, [data, attributeConfig, brand]);

    useEffect(() => {
        if (attributeConfig.hasArticleInfos && data) {
            const handleArticleInfos = (event: CustomEvent) => {
                if (isSPFArticleInfosEvent(event)) {
                    const sparePartsSortedWithPrice: SPFArticleWithPriceInfo[] = data.spareParts.nodes.map((item) => ({
                        ...item,
                        clientArticleInfo: undefined,
                    }));
                    event.detail.infos?.forEach((info: unknown) => {
                        const articleInfo = isSPFArticleInfo(info);
                        if (articleInfo) {
                            sparePartsSortedWithPrice.map((item) => {
                                if (item.article?.articleNumber === articleInfo.articleNumber) {
                                    item.clientArticleInfo = articleInfo;
                                }
                            });
                        }
                        setSparePartsWithPriceInfo(sparePartsSortedWithPrice);
                    });
                }
            };

            document.addEventListener(Events.SPFArticleInfos, handleArticleInfos);

            return () => document.removeEventListener(Events.SPFArticleInfos, handleArticleInfos);
        }
    }, [data, attributeConfig]);

    const hotspots = useMemo(() => {
        return uniqBy(
            data?.spareParts.nodes.reduce((result: GQLHotspotFragment[], sparePart: GQLSubGroupDetailSparePartFragment) => {
                return result.concat(sparePart.hotspots);
            }, []),
            "id",
        );
    }, [data]);

    const allHotspots = useMemo(() => {
        return uniqBy(
            data?.spareParts.nodes.reduce((result: GQLHotspotFragment[], sparePart: GQLSubGroupDetailSparePartFragment) => {
                return result.concat(sparePart.hotspots);
            }, []),
            "id",
        );
    }, [data?.spareParts.nodes]);

    const onRowMouseEnter = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, spareParts: GQLSubGroupDetailSparePartFragment[]): void => {
        const id = event.currentTarget.dataset.id;
        const row = spareParts.find((r) => r.id === id);
        if (row) {
            setHotspotsHighlight(row.hotspots);
        }
    };

    const onRowMouseLeave: React.MouseEventHandler<HTMLDivElement> = () => {
        setHotspotsHighlight([]);
    };

    const onHotspotMouseEnter = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, hotspot: GQLHotspotFragment): void => {
        setHotspotHighlight(hotspot);
    };

    const onHotspotMouseLeave: React.MouseEventHandler<HTMLDivElement> = () => {
        setHotspotHighlight(null);
    };

    const spareParts = useMemo(() => {
        return sparePartsWithPriceInfo.length > 0
            ? sortAndFilterSpareParts(sparePartsWithPriceInfo, searchTerm)
            : data
              ? sortAndFilterSpareParts(data.spareParts.nodes, searchTerm)
              : [];
    }, [data, sparePartsWithPriceInfo, searchTerm]);

    return (
        <ContainerBox data-testid={dataTestId.container}>
            <SplitView>
                <Box>
                    <Box data-testid={dataTestId.outerControlPanel}>
                        <ControlPanel
                            engineComponents={engineComponents}
                            frameComponents={frameComponents}
                            setOpenExplosionGraphic={setOpenExplosionGraphic}
                            modelArticleNumber={modelArticleNumber}
                        />
                    </Box>

                    {((isMediumScreen && !openExplosionGraphic) || !isMediumScreen) && (
                        <ListContainer>
                            <SearchBoxPanel data-testid={dataTestId.searchBoxContainer}>
                                <StyledSearchBox
                                    fullWidth
                                    data-testid={dataTestId.searchBox}
                                    placeholder={intl.formatMessage({
                                        id: "sparePartsFinder.subgroupPage.searchBox.placeholder",
                                        defaultMessage: "Search ...",
                                    })}
                                    value={searchTerm}
                                    onChange={(event) => setSearchTerm(event.target.value)}
                                    endAdornment={
                                        <IconButton variant="transparent" onClick={() => setSearchTerm("")} data-testid={dataTestId.btnClearSearch}>
                                            <Cross />
                                        </IconButton>
                                    }
                                />
                            </SearchBoxPanel>

                            {isSmallScreen ? (
                                loading && spareParts.length === 0 ? (
                                    Array(4)
                                        .fill(null)
                                        .map((_, index) => (
                                            <SmallListItemSkeleton key={`spare-part-list-item-skeleton-${index}`} showBorderBottom={index < 3} />
                                        ))
                                ) : (
                                    spareParts.map((item, index) => (
                                        <SmallListItem
                                            key={item.id}
                                            brand={brand}
                                            modelId={modelId}
                                            sparePart={item}
                                            showBorderBottom={index < spareParts.length - 1}
                                            clientWishlist={clientWishlist}
                                        />
                                    ))
                                )
                            ) : (
                                <SparePartsList
                                    modelId={modelId}
                                    brand={brand}
                                    loading={loading}
                                    error={error}
                                    sparePartsSorted={spareParts}
                                    onRowMouseEnter={onRowMouseEnter}
                                    onRowMouseLeave={onRowMouseLeave}
                                    hotspotHighlight={hotspotHighlight}
                                    clientWishlist={clientWishlist}
                                />
                            )}
                        </ListContainer>
                    )}
                </Box>

                {!isMediumScreen && selectedComponentGroup && (
                    <ExplosionGraphicBox>
                        <ExplosionGraphic
                            onMouseEnter={onHotspotMouseEnter}
                            onMouseLeave={onHotspotMouseLeave}
                            componentGroupItem={selectedComponentGroup}
                            hotspots={hotspots}
                            hotspotsHighlight={hotspotsHighlight}
                            canExpand={true}
                            showHotspots={true}
                            onToggleOpen={() => setOpenExplosionGraphic(!openExplosionGraphic)}
                        />
                    </ExplosionGraphicBox>
                )}

                {!isMediumScreen && selectedComponentGroup && (
                    <ExplosionGraphicModal
                        brand={brand}
                        modelId={modelId}
                        open={openExplosionGraphic}
                        componentGroupItem={selectedComponentGroup}
                        hotspots={allHotspots}
                        spareParts={spareParts}
                        onClickCancel={() => setOpenExplosionGraphic(!openExplosionGraphic)}
                        clientWishlist={clientWishlist}
                    />
                )}

                {isMediumScreen && selectedComponentGroup && openExplosionGraphic && (
                    <ExplosionGraphicSmall
                        brand={brand}
                        modelId={modelId}
                        componentGroupItem={selectedComponentGroup}
                        hotspots={allHotspots}
                        spareParts={spareParts}
                        clientWishlist={clientWishlist}
                    />
                )}
            </SplitView>
        </ContainerBox>
    );
};
